import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import type { Category, Product } from '@bazaar/shared';
import { api, ApiError } from '../lib/api.js';
import { useCart } from '../context/CartContext.js';
import { CategoryChips } from '../components/CategoryChips.js';
import { ProductTile } from '../components/ProductTile.js';
import { BottomBar } from '../components/BottomBar.js';
import { Spinner } from '../components/Spinner.js';
import { EmptyState } from '../components/EmptyState.js';
import { ScannerSheet } from '../components/ScannerSheet.js';

export function SellPage() {
  const navigate = useNavigate();
  const cart = useCart();

  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const { categories } = await api.listCategories();
      setCategories(categories);
    } catch {
      // Non-fatal: the "All" chip still works without categories loaded.
    }
  }, []);

  const loadProducts = useCallback(async (categoryId: number | null, q: string) => {
    setLoading(true);
    setError(null);
    try {
      const trimmed = q.trim();
      const { products } = await api.listProducts({
        categoryId: categoryId ?? undefined,
        q: trimmed.length >= 2 ? trimmed : undefined,
      });
      setProducts(products);
      for (const p of products) cart.updateStock(p.id, p.stockQty);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load products');
    } finally {
      setLoading(false);
    }
    // `cart` is a stable, memoised context value; only re-run on filter changes.
  }, []);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    void loadProducts(selectedCategoryId, search);
  }, [selectedCategoryId, search, loadProducts]);

  useEffect(() => {
    function onFocus() {
      void loadProducts(selectedCategoryId, search);
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [selectedCategoryId, search, loadProducts]);

  const visibleProducts = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle.length === 0) return products;
    return products.filter(
      (p) => p.name.toLowerCase().includes(needle) || p.code.toLowerCase().includes(needle),
    );
  }, [products, search]);

  function handleAdd(product: Product) {
    const line = cart.state.lines.find((l) => l.productId === product.id);
    if (line && line.qty >= product.stockQty) return;
    cart.addItem(product);
  }

  const scanButton = (
    <button
      type="button"
      onClick={() => setScannerOpen(true)}
      aria-label="Scan a product code"
      className={clsx(
        // font-bold text-xl: white-on-teal only clears WCAG AA at
        // >=18.66px bold - see the QA report, P3 #15.
        'fixed right-4 z-30 h-14 px-5 rounded-full bg-teal text-white shadow-lg flex items-center gap-2 font-heading font-bold text-xl',
        'lg:static lg:h-12 lg:shadow-none lg:shrink-0 lg:ml-auto',
        cart.totals.itemCount > 0 ? 'bottom-36 lg:bottom-auto' : 'bottom-20 lg:bottom-auto',
      )}
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
        <path
          d="M4 8V6a2 2 0 0 1 2-2h2M4 16v2a2 2 0 0 0 2 2h2M20 8V6a2 2 0 0 0-2-2h-2M20 16v2a2 2 0 0 1-2 2h-2M8 12h8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Scan
    </button>
  );

  return (
    <div className="flex flex-col">
      <div className="sticky-under-header z-20 bg-bg -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 pt-2 lg:pt-6">
        <CategoryChips
          categories={categories}
          selectedId={selectedCategoryId}
          onSelect={setSelectedCategoryId}
        />
        <div className="flex items-center gap-3 pb-2">
          <label htmlFor="product-search" className="sr-only">
            Search products
          </label>
          <input
            id="product-search"
            type="search"
            inputMode="search"
            placeholder="Search by name or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input flex-1 lg:max-w-xl"
          />
          {scanButton}
        </div>
      </div>

      {loading && products.length === 0 ? (
        <Spinner label="Loading products" />
      ) : error ? (
        <EmptyState title="Could not load products" description={error} />
      ) : visibleProducts.length === 0 ? (
        <EmptyState title="No products found" description="Try a different search or category." />
      ) : (
        <div
          className={clsx(
            'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 lg:gap-4 pt-4',
            // The sticky pink bottom bar (h-16 nav + BottomBar) can
            // otherwise cover the grid's last row on phones/tablets below
            // `lg` (the Scan button is inline in the toolbar from `lg` up,
            // so it no longer floats over the grid there). Reserve enough
            // space for the worst case (cart non-empty) plus the device's
            // safe-area inset so every tile always scrolls fully into view.
            cart.totals.itemCount > 0
              ? 'pb-[calc(8.5rem+env(safe-area-inset-bottom,0px))] lg:pb-28'
              : 'pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] lg:pb-8',
          )}
        >
          {visibleProducts.map((product) => (
            <ProductTile
              key={product.id}
              product={product}
              qtyInCart={cart.state.lines.find((l) => l.productId === product.id)?.qty ?? 0}
              onAdd={() => handleAdd(product)}
              onDecrement={() => cart.decrementItem(product.id)}
            />
          ))}
        </div>
      )}

      <BottomBar
        itemCount={cart.totals.itemCount}
        totalCents={cart.totals.totalCents}
        onReview={() => navigate('/cart')}
      />

      {scannerOpen && <ScannerSheet products={products} onClose={() => setScannerOpen(false)} />}
    </div>
  );
}
